/**
 * UniversityQuiz — take a course's quiz, then see the score and an explanation
 * for every question. Answers are graded on the server and only arrive with the
 * result.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import { RichText } from '../components/UniversityParts';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

function optionClass({ result, index, selected }) {
  if (result) {
    if (index === result.answer) return 'border-win bg-win/10';
    if (index === result.chosen) return 'border-loss bg-loss/10';
    return 'border-border opacity-60';
  }
  return selected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50';
}

function Question({ question, number, chosen, result, onChoose }) {
  return (
    <fieldset className="rounded-lg border border-border bg-card p-4">
      <legend className="sr-only">Question {number}</legend>
      <p className="text-sm font-medium mb-3">
        <span className="text-muted-foreground mr-1.5">{number}.</span>
        <RichText text={question.text} />
      </p>
      <div className="flex flex-col gap-1.5">
        {question.options.map((option, index) => (
          <label
            key={option}
            className={`flex items-start gap-2 rounded border px-3 py-2 text-sm transition-colors ${
              result ? '' : 'cursor-pointer'
            } ${optionClass({ result, index, selected: chosen === index })}`}
          >
            <input
              type="radio"
              name={question.id}
              className="mt-1"
              checked={chosen === index}
              disabled={Boolean(result)}
              onChange={() => onChoose(question.id, index)}
            />
            <span><RichText text={option} /></span>
          </label>
        ))}
      </div>
      {result && (
        <p className="mt-3 flex gap-2 text-xs leading-relaxed text-muted-foreground">
          {result.correct
            ? <CheckCircle2 className="h-4 w-4 text-win shrink-0" aria-label="Correct" />
            : <XCircle className="h-4 w-4 text-loss shrink-0" aria-label="Incorrect" />}
          <span><RichText text={result.why} /></span>
        </p>
      )}
    </fieldset>
  );
}

export default function UniversityQuiz() {
  const { course: courseSlug } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [answers, setAnswers] = useState({});
  const [graded, setGraded] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/university/courses/${courseSlug}/quiz`)
      .then((r) => setQuiz(r.data))
      .catch(() => setLoadFailed(true));
  }, [courseSlug]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/university/courses/${courseSlug}/quiz`, { answers });
      setGraded(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Couldn’t submit your answers. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => {
    setAnswers({});
    setGraded(null);
    window.scrollTo(0, 0);
  };

  const resultFor = (id) => graded?.results.find((r) => r.id === id);
  const allAnswered = quiz && quiz.questions.every((q) => answers[q.id] !== undefined);

  return (
    <AppLayout backTo={`/university/${courseSlug}`} backLabel="← Course" title="Quiz">
      <div className="max-w-2xl mx-auto p-4 md:p-6">
        {loadFailed && <p className="text-sm text-muted-foreground">That quiz couldn&apos;t be loaded.</p>}
        {!quiz && !loadFailed && <Skeleton className="h-64 rounded-lg" />}

        {quiz && (
          <>
            <h1 className="text-xl font-semibold">{quiz.title}</h1>
            <p className="text-xs text-muted-foreground mb-4">
              Course quiz · {quiz.pass_pct}% to pass · retake it as often as you like, and your best score counts.
            </p>

            {graded && (
              <div className={`rounded-lg border p-4 mb-4 ${graded.passed ? 'border-win bg-win/10' : 'border-border bg-card'}`}>
                <div className="text-2xl font-semibold">{Math.round(graded.score_pct)}%</div>
                <p className="text-sm mb-3">
                  {graded.passed
                    ? 'You passed. Review the explanations below, or head back to the course.'
                    : `You need ${quiz.pass_pct}% to pass. Read the explanations below, then try again.`}
                </p>
                <div className="flex flex-wrap gap-2">
                  {!graded.passed && <Button size="sm" onClick={retake}>Retake quiz</Button>}
                  <Button asChild size="sm" variant={graded.passed ? 'default' : 'secondary'}>
                    <Link to={graded.passed ? '/university' : `/university/${courseSlug}`}>
                      {graded.passed ? 'Back to all courses' : 'Review the course'}
                    </Link>
                  </Button>
                  {graded.passed && <Button size="sm" variant="secondary" onClick={retake}>Retake quiz</Button>}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {quiz.questions.map((question, i) => (
                <Question
                  key={question.id}
                  question={question}
                  number={i + 1}
                  chosen={answers[question.id]}
                  result={resultFor(question.id)}
                  onChoose={(id, index) => setAnswers((a) => ({ ...a, [id]: index }))}
                />
              ))}
            </div>

            {!graded && (
              <div className="mt-4 flex items-center justify-end gap-3">
                {!allAnswered && <span className="text-xs text-muted-foreground">Answer every question to submit.</span>}
                <Button size="sm" onClick={handleSubmit} disabled={!allAnswered || submitting}>
                  {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                  Submit answers
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
